import React, { useEffect, useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const normalizeHeader = (header) => (typeof header === "string" ? { label: header } : header);

export const ProgressTable = ({ children, headers, sortConfig, sortKey, sortDirection = "asc", onSort }) => {
  const theme = useTheme();
  const [showSwipeIndicators, setShowSwipeIndicators] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const activeSortConfig = sortConfig || { key: sortKey, direction: sortDirection };

  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent;
      const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      setIsMobile(iOS || isMobileDevice);
    };

    checkMobile();
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;
    const timer = setTimeout(() => {
      setShowSwipeIndicators(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isMobile]);

  return (
    <div
      className="relative flex flex-col w-full h-full overflow-hidden bg-clip-border rounded-[8px]"
      style={{
        backgroundColor: theme.mode === "dark" ? theme.colors?.backgroundColorNotCurrentModule : theme.colors?.background_secondary?.backgroundColor,
      }}
    >
      {isMobile && showSwipeIndicators && (
        <div
          className="absolute top-[2.2rem] left-0 right-0 flex justify-end px-4 z-10 pointer-events-none animate-pulse"
          style={{ transform: "translateY(-50%)" }}
        >
          <SwipeIcon
            sx={{
              fontSize: "3.8rem",
              color: "#fff",
              fill: theme.mode === "dark" ? "#000" : "#fff",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>
      )}

      <div
        className="w-full h-full overflow-auto rounded-[8px]"
        style={{
          backgroundColor: theme.mode === "dark" ? theme.colors?.backgroundColorNotCurrentModule : "#FFFFFF",
        }}
      >
        <table className="w-full text-left table-auto min-w-[700px] laptop:min-w-0 border-collapse">
          <thead>
            <tr
              style={{
                backgroundColor: theme.mode === "dark" ? theme.colors?.backgroundColorNotCurrentModule : "#FFFFFF",
                borderBottom: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
              }}
            >
              {headers && headers.map((header, idx) => {
                const h = normalizeHeader(header);
                const key = h?.key || h?.sortKey || null;
                const sortable = Boolean(key && h?.sortable !== false && typeof onSort === "function");
                const isSorted = Boolean(activeSortConfig?.key === key);
                const direction = isSorted ? activeSortConfig.direction : null;
                const justifyClass = h?.align === "center" ? "justify-center" : "";
                const headerStyle = {
                  ...(h?.width ? { width: h.width, minWidth: h.width } : {}),
                  ...(h?.align ? { textAlign: h.align } : {}),
                };

                return (
                  <th
                    className={`p-4 border-none ${sortable ? "cursor-pointer select-none group transition-colors" : ""}`}
                    key={key || idx}
                    scope="col"
                    style={Object.keys(headerStyle).length ? headerStyle : undefined}
                    onClick={sortable ? () => onSort(key) : undefined}
                    aria-sort={isSorted ? (direction === "asc" ? "ascending" : "descending") : "none"}
                    role={sortable ? "button" : undefined}
                    tabIndex={sortable ? 0 : undefined}
                    onKeyDown={sortable ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSort(key);
                      }
                    } : undefined}
                  >
                    <div className={`flex items-center gap-1.5 ${justifyClass}`}>
                      <p
                        className="block text-[11px] leading-none font-medium uppercase transition-colors whitespace-nowrap"
                        style={{
                          color: isSorted
                            ? (theme.mode === "dark" ? "#FFFFFF" : "#111827")
                            : "#757575",
                        }}
                      >
                        {h?.label}
                      </p>
                      {sortable && (
                        <span className={`inline-flex items-center transition-all duration-150 ${isSorted ? "opacity-100 scale-100" : "opacity-0 group-hover:opacity-40 scale-90"}`}>
                          {isSorted && direction === "desc" ? (
                            <ArrowDownwardIcon sx={{ fontSize: 14, color: theme.mode === "dark" ? "#FFFFFF" : "#111827" }} />
                          ) : (
                            <ArrowUpwardIcon sx={{ fontSize: 14, color: isSorted ? (theme.mode === "dark" ? "#FFFFFF" : "#111827") : "#757575" }} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
};
